<template>
  <b-sidebar
    :id="drawer.id"
    :title="drawerTitle"
    backdrop-variant="dark"
    backdrop
    right
    shadow
    no-close-on-backdrop
    no-close-on-esc
    width="620px"
    :visible="drawer.visible"
    sidebar-class="right-drawer white-drawer"
    aria-hidden="false"
    @hidden="onClose"
    @shown="handleDrawerShown"
  >
    <div class="p-2">
      <ValidationObserver
        ref="formObserver"
        #default="{ invalid }"
      >
        <section class="mb-[2rem]">
          <div class="space-y-[.75rem]">
            <!-- Partner -->
            <BFormGroup
              label="Partner"
              class="text-black font-medium"
            >
              <BFormInput
                id="partner_name"
                v-model="form.partner"
                :disabled="true"
              />
            </BFormGroup>

            <!-- Nomor Invoice (Only shown for Input Nomor Invoice mode) -->
            <BFormGroup
              v-if="isInputInvoiceNumberMode"
              class="text-black font-medium"
            >
              <template #label>
                Nomor Invoice<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Nomor Invoice"
                rules="required"
              >
                <div class="relative">
                  <BFormInput
                    v-model="form.invoiceNumber"
                    placeholder="Masukkan nomor invoice"
                    :class="{ 'pr-10': isLoadingInvoiceDetail }"
                    @keydown="getInvoiceDetailDebounced"
                  />
                  <div
                    v-if="isLoadingInvoiceDetail"
                    class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none"
                  >
                    <BSpinner
                      small
                      variant="primary"
                    />
                  </div>
                </div>
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Periode -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Periode<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Periode"
                rules="required"
              >
                <v-select
                  v-model="form.period"
                  :options="periodOptions"
                  :selectable="isPeriodSelectable"
                  :clearable="false"
                  placeholder="Pilih periode"
                  :disabled="isInputInvoiceNumberMode"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Tipe Pembayaran -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Tipe Pembayaran<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Tipe Pembayaran"
                rules="required"
              >
                <v-select
                  v-model="form.paymentType"
                  :options="paymentTypeOptions"
                  :clearable="false"
                  placeholder="Pilih tipe pembayaran"
                  :disabled="isInputInvoiceNumberMode"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Tanggal Jatuh Tempo -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Tanggal Jatuh Tempo<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                v-slot="{ errors }"
                rules="required"
                name="Tanggal Jatuh Tempo"
                class="w-full"
              >
                <input
                  v-model="form.dueDate"
                  type="date"
                  class="form-control w-full"
                  :class="{ 'is-invalid': errors.length > 0 }"
                  placeholder="Pilih Tanggal"
                  :min="new Date().toISOString().split('T')[0]"
                  :disabled="isInputInvoiceNumberMode"
                >
                <span
                  v-if="errors.length"
                  class="text-danger d-block mt-[2px] text-sm font-normal"
                >
                  {{ errors[0] }}
                </span>
              </ValidationProvider>
            </BFormGroup>

            <!-- Pilih Talent -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Pilih Talent<span class="text-danger">*</span>
              </template>
              <BFormInput
                id="talent_name"
                v-model="form.talent"
                :disabled="true"
              />
            </BFormGroup>

            <!-- Biaya Admin -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Biaya admin<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Biaya admin"
                rules="required"
              >
                <v-select
                  v-model="form.adminFee"
                  :options="adminFeeOptions"
                  taggable
                  placeholder="Pilih atau Masukkan biaya admin"
                  :disabled="isInputInvoiceNumberMode"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Diskon -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Diskon
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Diskon"
                rules="min_value:0"
              >
                <BFormInput
                  v-model="form.discount"
                  placeholder="Rp0"
                  type="number"
                  min="0"
                  :disabled="isInputInvoiceNumberMode"
                  @keydown="preventNegativeKey"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Gaji Talent -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Gaji Talent<span class="text-danger">*</span>
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Gaji Talent"
                rules="required|min_value:0"
              >
                <BFormInput
                  v-model="form.talentSalary"
                  placeholder="Rp0"
                  type="number"
                  min="0"
                  :disabled="isInputInvoiceNumberMode"
                  @keydown="preventNegativeKey"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <!-- Biaya lainnya -->
            <BFormGroup class="text-black font-medium">
              <template #label>
                Biaya lainnya
              </template>
              <ValidationProvider
                #default="{ errors }"
                name="Biaya lainnya"
                rules="min_value:0"
              >
                <BFormInput
                  v-model="form.otherFee"
                  placeholder="Rp0"
                  type="number"
                  min="0"
                  :disabled="isInputInvoiceNumberMode"
                  @keydown="preventNegativeKey"
                />
                <small class="text-danger d-block mt-[2px]">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>
          </div>

          <!-- Calculation Summary Breakdown -->
          <div class="mt-[1.5rem] pt-[1rem] border-t border-dashed border-[#E5E5E5] text-right space-y-1 text-sm text-[#525252]">
            <div class="font-medium text-black">
              Total Biaya Admin : {{ formatRp(totalAdminFeeNet) }}
              <span class="font-normal">({{ formatRp(adminFeeGross) }} - {{ formatRp(discountAmount) }})</span>
            </div>
            <div class="text-[#8C8C8C]">
              PPN 11% : {{ formatRp(ppnAmount) }}
            </div>
            <div class="font-medium text-black">
              Total Setelah PPN : {{ formatRp(totalAfterPpn) }}
            </div>
            <div class="font-medium text-black">
              Total Gaji Talent : {{ formatRp(talentSalaryAmount) }}
            </div>
            <div class="text-[#525252]">
              Xendit (Layanan Admin): {{ formatNumber(xenditFee) }}
            </div>
            <div class="font-semibold text-[#10B981]">
              Total Nominal : {{ formatRp(totalNominal) }}
            </div>
            <div class="flex items-center justify-end gap-2">
              <BFormCheckbox
                v-model="form.enablePph23"
                class="text-[#525252]"
              >
                Kenakan Pph 23
              </BFormCheckbox>
            </div>
          </div>
        </section>

        <!-- Footer Buttons -->
        <div class="flex gap-[.75rem] pt-[1rem] border-t border-[#E5E5E5]">
          <BButton
            variant="light"
            block
            class="py-2.5 rounded-xl font-medium"
            @click="onClose"
          >
            Batalkan
          </BButton>
          <BButton
            variant="primary"
            block
            :disabled="invalid || loadingSubmit"
            class="mt-0! bg-[#EC5A24] border-0 py-2.5 rounded-xl font-medium"
            @click="handleSubmit"
          >
            <BSpinner
              v-if="loadingSubmit"
              small
            />
            <span v-else>Buat Invoice</span>
          </BButton>
        </div>
      </ValidationObserver>
    </div>
  </b-sidebar>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import VSelect from 'vue-select'
import '@core/scss/vue/libs/vue-select.scss'
import { komtimAxiosIns } from '@/libs/axios'
import {
  GENERATE_MONTH_OPTIONS_ID, YEAR_MONTH_ID, YEAR_MONTH_DAY, NODASH_MONTH_YEAR,
} from '@/libs/filterDate'
import moment from 'moment'
import { debounce } from 'lodash'
import { INVOICE_ACTION, showSuccess, showError } from '../config'

export default {
  name: 'InvoiceDrawer',
  components: {
    ValidationObserver,
    ValidationProvider,
    VSelect,
  },
  props: {
    drawer: {
      type: Object,
      default: () => ({
        visible: false,
        id: 'invoice-drawer',
        mode: INVOICE_ACTION.MAKE_INV,
        data: () => ({}),
      }),
    },
  },
  data() {
    return {
      required,
      loadingSubmit: false,
      isLoadingTalents: false,
      isLoadingInvoiceDetail: false,
      listTalent: [],
      rawAdminFeeOptions: [],
      form: {
        partner: '',
        invoiceNumber: '',
        period: '',
        paymentType: null,
        dueDate: '',
        talent: null,
        adminFee: '',
        discount: '',
        talentSalary: '',
        otherFee: '',
        enablePph23: false,
      },
      paymentTypeOptions: ['Pembayaran Awal', 'Pembayaran Akhir'],
      adminFeeOptions: [],
      xenditFee: 4440,
    }
  },
  computed: {
    periodOptions() {
      return GENERATE_MONTH_OPTIONS_ID()
    },
    drawerTitle() {
      return this.drawer.title
    },
    isInputInvoiceNumberMode() {
      return this.drawer.title === INVOICE_ACTION.INPUT_INV_NUMB
    },
    adminFeeGross() {
      return Math.max(0, parseFloat(this.form.adminFee) || 0)
    },
    discountAmount() {
      return Math.max(0, parseFloat(this.form.discount) || 0)
    },
    totalAdminFeeNet() {
      return Math.max(0, this.adminFeeGross - this.discountAmount)
    },
    ppnAmount() {
      return Math.round(this.totalAdminFeeNet * 0.11)
    },
    totalAfterPpn() {
      return this.totalAdminFeeNet + this.ppnAmount
    },
    talentSalaryAmount() {
      return Math.max(0, parseFloat(this.form.talentSalary) || 0)
    },
    otherFeeAmount() {
      return Math.max(0, parseFloat(this.form.otherFee) || 0)
    },
    totalNominal() {
      let total = this.totalAfterPpn + this.talentSalaryAmount + this.otherFeeAmount + this.xenditFee
      if (this.form.enablePph23) {
        total -= Math.round(this.totalAdminFeeNet * 0.02)
      }
      return total
    },
  },
  created() {
    this.getInvoiceDetailDebounced = debounce(this.getInvoiceDetail, 500)
  },
  methods: {
    preventNegativeKey(e) {
      if (['-', '+', 'e', 'E'].includes(e.key)) {
        e.preventDefault()
      }
    },
    isPeriodSelectable(option) {
      const monthIndex = this.periodOptions.indexOf(option)
      if (monthIndex === -1) return true
      const maxAllowedMonthIndex = moment().month() + 1
      return monthIndex <= maxAllowedMonthIndex
    },
    formatRp(val) {
      const num = Math.round(Number(val) || 0)
      return `Rp ${num.toLocaleString('id-ID')}`
    },
    formatNumber(val) {
      const num = Number(val) || 0
      return num.toLocaleString('id-ID')
    },
    onClose() {
      this.resetForm()
      this.$emit('on-close')
    },
    handleDrawerShown() {
      this.form.partner = this.drawer.data.partner_name
      this.form.talent = this.drawer.data.talent.name
      this.getListAdminFee()
    },
    async getListAdminFee() {
      const params = `limit=20&skill=${this.drawer.data?.talent?.skill_id || ''}`
      const url = `v1/admin_fees?${params}`
      try {
        const res = await komtimAxiosIns.get(url)
        const { data } = res.data
        this.rawAdminFeeOptions = data || []
        if (data && data.length > 0) {
          this.adminFeeOptions = data.map(item => item.admin_fee)
        }
      } catch {
        showError({ title: 'Terjadi kesalahan' })
      }
    },
    async getListTalent(keyword) {
      this.isLoadingTalents = true
      try {
        const url = `v1/talents?offset=0&limit=20&keyword=${keyword}`
        const res = await komtimAxiosIns.get(url)
        this.listTalent = res.data?.data || []
      } catch {
        // quiet error fallback
      } finally {
        this.isLoadingTalents = false
      }
    },
    onSearchTalent(search) {
      this.getListTalent(search)
    },
    async getInvoiceDetail(codeParam) {
      const code = typeof codeParam === 'string' ? codeParam : this.form.invoiceNumber
      if (!code) return
      try {
        this.isLoadingInvoiceDetail = true
        const url = `/v1/invoices/detail?code=${code}`
        const res = await komtimAxiosIns.get(url)
        const resData = res.data?.data || {}
        const detailItem = (Array.isArray(resData.data) && resData.data.length > 0) ? resData.data[0] : {}

        if (resData.partner_name) {
          this.form.partner = resData.partner_name
        }
        if (resData.invoice_period) {
          this.form.period = NODASH_MONTH_YEAR(resData.invoice_period)
        }
        if (typeof resData.is_early_payment === 'boolean') {
          this.form.paymentType = resData.is_early_payment ? 'Pembayaran Awal' : 'Pembayaran Akhir'
        }
        if (resData.due_date) {
          this.form.dueDate = YEAR_MONTH_DAY(resData.due_date)
        }
        if (detailItem.talent_id && detailItem.talent_name) {
          const talentObj = {
            id: detailItem.talent_id,
            full_name: detailItem.talent_name,
            name: detailItem.talent_name,
          }
          this.listTalent = [talentObj]
          this.form.talent = talentObj.full_name
        }
        if (detailItem.admin_fee !== undefined) {
          this.form.adminFee = detailItem.admin_fee
          if (detailItem.admin_fee_id) {
            const rawItem = {
              id: detailItem.admin_fee_id,
              admin_fee: detailItem.admin_fee,
              name: detailItem.admin_fee_name || '',
            }
            this.rawAdminFeeOptions = [rawItem]
            this.adminFeeOptions = [detailItem.admin_fee]
          }
        } else if (resData.total_admin_fee !== undefined) {
          this.form.adminFee = resData.total_admin_fee
        }
        if (detailItem.discount !== undefined) {
          this.form.discount = detailItem.discount
        }
        if (detailItem.talent_salary !== undefined) {
          this.form.talentSalary = detailItem.talent_salary
        } else if (resData.total_salary_talent !== undefined) {
          this.form.talentSalary = resData.total_salary_talent
        }
        if (detailItem.additional_cost !== undefined) {
          this.form.otherFee = detailItem.additional_cost
        }
        if (resData.total_tax !== undefined) {
          this.form.enablePph23 = Number(resData.total_tax) > 0
        }
        if (resData.admin_service !== undefined) {
          this.xenditFee = resData.admin_service
        }
      } catch (error) {
        this.resetForm({ excepts: ['invoiceNumber'] })
        showError({ title: 'Nomor invoice tidak ditemukan!' })
      } finally {
        this.isLoadingInvoiceDetail = false
      }
    },
    resetForm(config = { excepts: [] }) {
      const defaultForm = {
        partner: this.drawer.data?.partner_name,
        invoiceNumber: '',
        period: '',
        paymentType: null,
        dueDate: '',
        talent: this.drawer.data?.talent_name,
        adminFee: '',
        discount: '',
        talentSalary: '',
        otherFee: '',
        enablePph23: false,
      };
      (config?.excepts || []).forEach(ex => {
        delete defaultForm[ex]
      })
      this.form = { ...this.form, ...defaultForm }
      this.$nextTick(() => {
        if (this.$refs.formObserver) {
          this.$refs.formObserver.reset()
        }
      })
    },
    async handleSubmit() {
      if (this.isInputInvoiceNumberMode) {
        await this.updateInvoiceNumber()
      } else {
        await this.createInvoice()
      }
    },
    async updateInvoiceNumber() {
      try {
        this.loadingSubmit = true
        const id = this.drawer.data?.id || 0
        const payload = {
          invoice_code: this.form.invoiceNumber,
        }
        await komtimAxiosIns.put(`/v1/partner_assignment_requests/${id}/invoice`, payload)
        showSuccess({ title: 'Berhasil menyimpan Nomor Invoice!' })
        this.$emit('on-success')
        this.onClose()
      } catch {
        showError({ title: 'Gagal menyimpan Nomor Invoice!' })
      } finally {
        this.loadingSubmit = false
      }
    },
    async createInvoice() {
      try {
        this.loadingSubmit = true

        const selectedAdminFeeObj = (this.rawAdminFeeOptions || []).find(
          item => Number(item.admin_fee) === Number(this.form.adminFee),
        )
        const adminFeeId = selectedAdminFeeObj ? selectedAdminFeeObj.id : 0

        const periodFormatted = this.form.period
          ? YEAR_MONTH_ID(this.form.period)
          : ''

        const dueDateFormatted = this.form.dueDate
          ? YEAR_MONTH_DAY(this.form.dueDate)
          : ''

        const totalDetail = this.totalAdminFeeNet + this.talentSalaryAmount + this.otherFeeAmount

        const payload = {
          partner_id: this.drawer.data?.partner?.id || 0,
          partner_assignment_request_id: this.drawer.data?.id || 0,
          invoice_period: periodFormatted,
          invoice_details: [
            {
              id: null,
              talent_name: this.drawer.data?.talent?.name,
              talent_id: this.drawer.data?.talent?.id,
              admin_fee_id: adminFeeId,
              admin_fee: this.adminFeeGross,
              discount: this.discountAmount,
              talent_salary: this.talentSalaryAmount,
              total: totalDetail,
              additional_cost: this.otherFeeAmount,
              additional_cost_type: 'talent',
            },
          ],
          is_tax: Boolean(this.form.enablePph23),
          total_admin_fee: this.totalAdminFeeNet,
          total_talent_salary: this.talentSalaryAmount,
          admin_service_fee: this.xenditFee,
          total_nominal: this.totalNominal,
          is_early_payment: this.form.paymentType === 'Pembayaran Awal',
          notes: '',
          hide_costs: false,
          due_date: dueDateFormatted,
        }

        await komtimAxiosIns.post('/v1/invoices/store', payload)
        showSuccess({ title: 'Berhasil membuat Invoice!' })
        this.$emit('on-success')
        this.onClose()
      } catch {
        showError({ title: 'Gagal membuat invoice!' })
      } finally {
        this.loadingSubmit = false
      }
    },
  },
}
</script>

<style scoped>
.btn-primary {
  background-color: #EC5A24 !important;
  border-color: #EC5A24 !important;
  border-radius: 10px;
}
.btn-light {
  border-radius: 10px;
}
::v-deep .white-drawer .b-sidebar-header,
::v-deep .white-drawer .b-sidebar-body {
  background-color: white;
}
</style>
